import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import FontAwesomeIcon from "app/components/layouts/Icons/FontAwesome";
import { useState, useMemo, useEffect, useRef } from 'react';
import Steps from 'app/components/tests/Ielts/IeltSpeak/Steps';
import Instruct from 'app/components/speaking/Instruct';
import { getMobileOperatingSystem } from 'app/utils/ga';
import { setAuthentication } from 'app/actions/commons';
import { useDispatch, useSelector } from 'react-redux';
import { toastWarning } from 'app/utils/toastHelper';
import * as commonActions from 'app/actions/commons';
import * as testConst from 'app/constants/tests';
import { AppStoreState } from 'app/redux/store';
import { useTranslation } from 'next-i18next';
import * as workerTimers from 'worker-timers';
import { Question, Group } from 'types/test';
import * as testApi from 'app/requests/tests';
import { position } from 'app/utils/dom';
import { useRouter } from 'next/router';
import storage from 'app/utils/storage';
import env from '.env.json';

import dynamic from "next/dynamic";
const ModalDevice = dynamic(() => import("app/components/tests/modal/ModalDevice"), { ssr: false });
const ModalMicro = dynamic(() => import("app/components/tests/modal/ModalMicro"), { ssr: false });
const BarSound = dynamic(() => import("app/components/layouts/BarSound/Index"), { ssr: false });

interface AudioData {
  times: string | number[];
  s_times: number[];
  url: string;
}

const index = () => {
  const { t } = useTranslation();
  const [openModalDevice, setOpenModalDevice] = useState(false);
  const [openModalMicro, setOpenModalMicro] = useState(false);
  const [currentTime, setCurrentTime] = useState([0, 0, 0]);
  const [indexQuestion, setIndexQuestion] = useState<number | null>(null);
  const [timePending, setTimePending] = useState<number>(0);
  const [indexGroup, setIndexGroup] = useState<number | null>(null);
  const [collapseId, setCollapseId] = useState<number | null>(null);
  const [process, setProcess] = useState<string>('init');
  const [audioData, setAudioData] = useState<AudioData[]>([]);
  const [record, setRecord] = useState<string | null>(null);
  const dispatch = useDispatch();

  var timeoutThinking: number | null = null;
  const router = useRouter()
  const audioPlayer = useRef<React.ElementRef<"audio">[]>([]);
  const { auth } = useSelector((state: AppStoreState) => state.global);
  const { test, groupQuestions } = useSelector((state: AppStoreState) => state.test);

  const titles = {
    part_1: t('ielt_speak_instructions.part1'),
    part_2: t('ielt_speak_instructions.part2'),
    part_3: t('ielt_speak_instructions.part3')
  };

  useEffect(() => {
    setTimeout(() => {
      if (timePending > 0) {
        setTimeoutPending(timePending);
      }
    }, 0);
  }, [timePending])

  useEffect(() => {
    fetchIeltSpeak();
    if (groupQuestions.length) {
      setCollapseId(groupQuestions[0]?.id)
    }

    return () => {
      clearTimeThinkingWorker();
      setAudioData([]);
    };
  }, [])

  const fetchIeltSpeak = async () => {
    const result = await testApi.ieltsSpeakGet(test.id);
    setAudioData(genarateAudioData(result as AudioData[]));
  };

  const genarateAudioData = (audios: AudioData[]) => {
    return (audios || []).map(audio => {
      const times = (audio['times'] as string).split(',').map((time: string) => Number(time) / 1000);
      audio['times'] = times;
      audio['s_times'] = times.map((tịme: number, index: number) => {
        return times.slice(0, index + 1).reduce((a: number, b: number) => a + b, 0);
      });

      return audio;
    })
  }

  const onPlaying = (groupIndex: number) => {
    const newCurrentTime = [0, 0, 0];
    newCurrentTime[groupIndex] = audioPlayer.current[groupIndex].currentTime;
    setCurrentTime(newCurrentTime);
  };

  const setPositionTime = (groupIndex: number, time: number) => {
    audioPlayer.current[groupIndex].currentTime = time;
    audioPlayer.current[groupIndex].play();
  }

  const setTimeoutPending = (time: number) => {
    if (record == 'start') {
      onStartMicroPhone();
      return;
    }

    timeoutThinking = workerTimers.setTimeout(() => {
      if (timePending > 0) {
        const newTime = timePending - 1;
        if (newTime > 0) {
          setTimePending(newTime)
        } else {
          onStartMicroPhone();
        }
      } else {
        setTimePending(time)
      }

      clearTimeThinkingWorker();
    }, 1000);
  }

  const onStartMicroPhone = () => {
    setTimePending(0);
    setRecord('start');
    clearTimeThinkingWorker();
  }

  const clearTimeThinkingWorker = () => {
    if (timeoutThinking != null) {
      workerTimers.clearTimeout(timeoutThinking);
      timeoutThinking = null;
    }
  }

  const setNextQuestion = (next: string, newGroupIndex: number | null, newQuestionIndex: number | null = null) => {
    setProcess(next)
    setIndexGroup(newGroupIndex);
    setIndexQuestion(newQuestionIndex);
  }

  const checkMicrophone = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (error) {
      setOpenModalMicro(true);
      return false;
    }
  }

  const handleProcess = async (regexProcess: RegExpMatchArray | null = null) => {
    const resetToPartNumber = regexProcess?.length ? Number(regexProcess[0]) : 0;
    if (record == 'start') setRecord('pause');
    if (!Object.keys(auth).length) {
      storage.set("visitUrl", router.asPath)
      toastWarning(t('auth.login_require'));
      router.push('/auth/login');
    }

    if (getMobileOperatingSystem()) {
      setOpenModalDevice(true);
      return;
    }

    const hasMicro = await checkMicrophone();
    if (!hasMicro) return;

    const initPart = () => {
      setNextQuestion('part_1', null, null)
      if (audioData.length > 0) {
        setAudioData([]);
        testApi.ieltsSpeakDelete(test.id);
      }
    }

    const completePart = (partId: number) => {
      setRecord('stop');
      setNextQuestion(`part_${partId}_next_part`, null, null);
    }

    const completeTest = async () => {
      try {
        dispatch(commonActions.setLoading(true));
        const [audios, user] = await testApi.ieltsSpeakStore({ data: audioData, testId: test.id }) as any;
        dispatch(setAuthentication(user));
        setAudioData(genarateAudioData(audios));
        setNextQuestion('review', null, null)
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(commonActions.setLoading(false));
      }
    }

    const nextGroupQuestion = (indexGroup: number) => {
      const maxQuestionNumber = groupQuestions[indexGroup]?.list_question?.length;
      const partId = indexGroup + 1;
      const nextQuestionIndex = indexQuestion! + 1;

      const timePending = partId == testConst.PART_2 ? testConst.TIME_PENDING_PART_2 : testConst.TIME_PENDING_PART_1;
      setTimePending(timePending);

      const newIndexQuestion = indexQuestion != null ? nextQuestionIndex : 0;
      if (maxQuestionNumber === newIndexQuestion + 1) {
        setNextQuestion(`part_${partId}_complete_part`, indexGroup, newIndexQuestion);
      } else {
        setNextQuestion(`part_${partId}_next_question`, indexGroup, newIndexQuestion);
      }
    }

    switch (true) {
      case process === 'init' || process === 'review' || resetToPartNumber == testConst.PART_1:
        initPart();
        break;
      case process === 'part_1_next_part' || resetToPartNumber == testConst.PART_2:
        setNextQuestion('part_2', null, null)
        break;
      case process === 'part_2_next_part' || resetToPartNumber == testConst.PART_3:
        setNextQuestion('part_3', null, null)
        break;
      case process === 'part_3_next_part':
        completeTest();
        break;
      case process.replace('_next_question', '') === 'part_1':
        nextGroupQuestion(0);
        break;
      case process.replace('_next_question', '') === 'part_2':
        nextGroupQuestion(1);
        break;
      case process.replace('_next_question', '') === 'part_3':
        nextGroupQuestion(2);
        break;
      case process.replace('_complete_part', '') === 'part_1':
        completePart(testConst.PART_1);
        break;
      case process.replace('_complete_part', '') === 'part_2':
        completePart(testConst.PART_2);
        break;
      case process.replace('_complete_part', '') === 'part_3':
        completePart(testConst.PART_3);
        break;
    }
  }

  const genarateButton = useMemo(() => {
    switch (true) {
      case process === 'init':
        return audioData.length > 0 ? t('tests.retest') : t('tests.ready')
      case process === 'review':
        return t('tests.retest')
      case ['part_1', 'part_2', 'part_3'].includes(process):
        return t('actions.start');
      case process.includes('next_question'):
        return t('ielt_speak_instructions.next_question');
      case process.includes('complete_part'):
        return t('ielt_speak_instructions.complete_part', { part: indexGroup! + 1 });
      case process.includes('part_3_next_part'):
        return t('tests.submit');
      case process.includes('next_part'):
        return t('ielt_speak_instructions.next_part');
    }
  }, [process, audioData, router?.locale]);

  const time = useMemo(() => {
    return new Date(timePending * 1000).toISOString().slice(14, 19);
  }, [timePending]);

  const genarateQuestion = (group: Group, groupIndex: number) => {
    const isReview = process === 'review';
    return group.list_question.map((question: Question, questionIndex: number) => {
      const timeQuestion = audioData[groupIndex]?.s_times[questionIndex];
      const prevTime = questionIndex == 0 ? 0 : audioData[groupIndex]?.s_times[questionIndex - 1];
      const isForcus = prevTime < currentTime[groupIndex] && currentTime[groupIndex] < timeQuestion;
      const timePlay = audioData[groupIndex]?.times[questionIndex] as number;

      return (
        <div key={question.id}>
          <li className={`d-flex ${!isReview ? 'recording__question-item' : ''}${isForcus ? ' text-info font-weight-bold' : ''}`}>
            {isForcus && isReview &&
              <BarSound />
            }
            <p className={`${isReview ? 'has-audio' : ''}`}
              onClick={() => isReview && timePlay > 0 ? setPositionTime(groupIndex, prevTime) : {}}>
              {!isForcus && isReview && <span className="fa fa-play text-info pr-2 ml-1"></span>}
              {question.title.replace(/<[^>]*>/g, "")}
            </p>
            {isReview && timePlay &&
              <span className="text-info ml-auto pl-2">{timePlay.toFixed(1) || 0}s</span>
            }
          </li>
          <i style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: question.explain! }} />
        </div>
      );
    });
  }

  const showTopicQuestion = (groupId: number) => {
    setCollapseId(groupId == collapseId ? null : groupId);
    if (groupId !== collapseId) {
      setTimeout(() => {
        window.scrollTo({ top: position(`topic_${groupId}`)?.top - 50, behavior: 'smooth' });
      }, 0);
    }
  }

  return (
    <div className="speaking-review-page" id="accordion">
      {['init', 'review'].includes(process) &&
        <>
          <h2 className="recordings-panel__title">
            {t(`ielt_speak_instructions.${process === 'init' ? 'question_list' : 'your_record'}`)}
          </h2>

          {(groupQuestions || []).map((group: Group, groupIndex: number) => {
            const audioUrl = audioData && audioData[groupIndex] && audioData[groupIndex].url ? audioData[groupIndex].url : null;
            return (
              <div className="card card-question" key={group.id}>
                <div
                  className="card-header d-flex"
                  id={`topic_${group.id}`}
                  onClick={() => showTopicQuestion(group.id)}
                >
                  <h2 className={`mb-0 text-info cursor-pointer`}>
                    {titles[`part_${groupIndex + 1}`]}
                  </h2>
                  <FontAwesomeIcon
                    size="lg"
                    className="ml-auto mt-3 text-info"
                    icon={collapseId == group.id ? faAngleDown : faAngleUp}
                  />
                </div>
                <div className={`collapse ${collapseId == group.id ? 'show' : ''}`} data-parent="#accordion">
                  <div className="card-body">
                    {audioUrl && process === 'review' &&
                      <audio
                        onTimeUpdate={() => onPlaying(groupIndex)}
                        onEnded={() => setCurrentTime([0, 0, 0])}
                        onPause={() => setCurrentTime([0, 0, 0])}
                        className="mb-3"
                        style={{ width: '100%' }} controls ref={el => {
                          audioPlayer.current[groupIndex] = el as any;
                        }}
                        src={`${env.URL_APP_FILE || env.URL_API}/storage/${audioUrl}`}
                      />
                    }
                    <ul className="recording__question-list">
                      {genarateQuestion(group, groupIndex)}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      }
      {!['init', 'review'].includes(process) &&
        <Steps
          titles={titles}
          record={record}
          setRecord={setRecord}
          timePending={timePending}
          process={process}
          indexGroup={indexGroup}
          indexQuestion={indexQuestion}
          groupQuestions={groupQuestions}
          audioData={audioData}
          setAudioData={setAudioData}
          setNextQuestion={setNextQuestion}
        />
      }
      <div className="text-center mt-3">
        <p className={`text-warning font-weight-bold ${timePending > 0 && indexQuestion ? '' : 'd-none'}`}>
          {t('ielt_speak_instructions.skip_time')}
        </p>
        <div className="d-flex justify-content-center">
          {process.includes('next_part') &&
            <button
              type="button"
              className={`btn btn-lg btn-danger px-5 mr-2`}
              onClick={() => handleProcess(process.match(/\d+/))}
            >
              {t('ielt_speak_instructions.reset_part')}
            </button>
          }
          {process === 'init' && audioData.length > 0 &&
            <button
              type="button"
              className={`btn btn-lg btn-success px-5 mr-2`}
              onClick={() => setNextQuestion('review', null, null)}
            >
              {t('ielt_speak_instructions.review')}
            </button>
          }
          <button
            type="button"
            className={`btn ${timePending > 0 ? 'btn-danger' : 'btn-info'} btn-lg px-5`}
            onClick={() => timePending > 0 ? onStartMicroPhone() : handleProcess()}
          >
            {timePending > 0 ? t('ielt_speak_instructions.time_to_think', { time }) : genarateButton}
          </button>
        </div>
      </div>
      <Instruct />
      <ModalMicro isOpen={openModalMicro} onClose={() => setOpenModalMicro(false)} />
      <ModalDevice isOpen={openModalDevice} onClose={() => setOpenModalDevice(false)} />
    </div>
  );
}

export default index
